// src/EvidencePage.jsx
import { useEffect, useRef, useState, useMemo, forwardRef } from "react";
import { Link, useSearchParams } from "react-router-dom";

// === Configure the correct god's name revealed in the Hidden Alert ===
// Change "ares" below to the actual name you reveal in hidden_alert.pdf (case-insensitive).
const FORGE_ALERT_GOD = "Hermes";

// Locations + hotspot positions on the map (each dot sits just below the label text)
const LOCATIONS = [
  { key: "parthenon",        name: "Parthenon",         x: "16%", y: "18%" },
  { key: "throne-room",      name: "Throne Room",       x: "56%", y: "26%" },
  { key: "hall-of-records",  name: "Hall of Records",   x: "58%", y: "47%" },
  { key: "oracles-chamber",  name: "Oracle's Chamber",  x: "55%", y: "63%" },
  { key: "ironheart-forge",  name: "IronHeart Forge",   x: "33%", y: "67%" },
  { key: "sacred-glade",     name: "Sacred Glade",      x: "60%", y: "86%" },
  { key: "veiled-crossroad", name: "Veiled Crossroads", x: "85%", y: "34%" },
];


/** THRONE ROOM gallery */
const THRONE_FILES = [1, 2, 3, 4, 5, 6, 7];

const THRONE_EVIDENCE = [
  // images
  ...THRONE_FILES.map((n) => ({
    id: `tr-${n}`,
    type: "image",
    title: `Photo ${n}`,
    src: `${process.env.PUBLIC_URL}/evidences/TR_evidence_${n}.JPEG`,
  })),
  // videos
  {
    id: "tr-zeus-video",
    type: "video",
    title: "Zeus Testimony (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/zeus-testimony.mp4`,
  },
  // Moved here: Feather video under Throne Room
  {
    id: "tr-feather",
    type: "video",
    title: "Feather (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/feather.mp4?v=1`,
  },
];

/** ORACLE'S CHAMBER */
const ORACLE_EVIDENCE = [
  {
    id: "oc-apollo-video",
    type: "video",
    title: "Apollo Testimony (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/apollos_testimony.mp4`,
  },
];

/** HALL OF RECORDS */
const HALL_EVIDENCE = [
  {
    id: "hr-hermes-video",
    type: "video",
    title: "Hermes Testimony (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/Hermes_testimony.mp4?v=1`,
  },
];

/** IRONHEART FORGE */
const FORGE_EVIDENCE = [
  {
    id: "if-hephaestus-video",
    type: "video",
    title: "Hephaestus Testimony (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/Hephaestus_testimony.mp4?v=1`,
  },
  // Locked PDF (Hidden Alert)
  {
    id: "if-hidden-alert",
    type: "pdf",
    title: "Hidden Alert",
    src: `${process.env.PUBLIC_URL}/evidences/hidden_alert.pdf`,
  },
];

/** SACRED GLADE */
const GLADE_EVIDENCE = [
  {
    id: "sg-artemis-video",
    type: "video",
    title: "Artemis Testimony (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/artemis_testimony.mp4?v=art1`,
  },
  {
    id: "sg-equation-video",
    type: "video",
    title: "Equation (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/equation.mp4?v=1`,
  },
];

/** VEILED CROSSROAD */
const VEILED_EVIDENCE = [
  {
    id: "vc-burnt-scroll",
    type: "video",
    title: "Burnt Scroll (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/burnt_scroll.mp4?v=1`,
  },
  {
    id: "vc-hecate-1",
    type: "video",
    title: "Hecate’s Testimony — Part I",
    src: `${process.env.PUBLIC_URL}/evidences/hecates_testimony_part1.mp4?v=1`,
  },
  {
    id: "vc-hecate-2",
    type: "video",
    title: "Hecate’s Testimony — Part II",
    src: `${process.env.PUBLIC_URL}/evidences/hecates_testimony_part2.mp4?v=1`,
  },
];

/** Parthenon of Olympus */
const PARTHENON_EVIDENCE = [
  {
    id: "po-athena-video",
    type: "video",
    title: "Athena's Testimony (Video)",
    src: `${process.env.PUBLIC_URL}/evidences/athenas_testimony.mp4`,
  },
  {
    id: "po-codex",
    type: "pdf",
    title: "Athenian Codex",
    src: `${process.env.PUBLIC_URL}/evidences/Athenian Codex.pdf`,
  },
  {
    id: "po-8",
    type: "image",
    title: "Photo 8",
    src: `${process.env.PUBLIC_URL}/evidences/PO_evidence_8.jpg`,
  },
  {
    id: "po-9",
    type: "image",
    title: "Photo 9",
    src: `${process.env.PUBLIC_URL}/evidences/PO_evidence_9.jpg`,
  },
];

export default function EvidencePage() {
  const [activeKey, setActiveKey] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const contentRef = useRef(null);

  // Password gate state
  const [pendingKey, setPendingKey] = useState(null);
  const [showGate, setShowGate] = useState(false);

  // Zeus quiz state
  const zeusVideoRef = useRef(null);
  const [showZeusQuiz, setShowZeusQuiz] = useState(false);
  const [zeusAnswer, setZeusAnswer] = useState(() => {
    try { return localStorage.getItem("zeusTestimonyAnswer") || ""; } catch { return ""; }
  });

  // Hermes inline quiz state (Hall of Records → Ironheart Forge)
  const hermesVideoRef = useRef(null);
  const [hermesAnswer, setHermesAnswer] = useState(() => {
    try { return localStorage.getItem("hermesTestimonyAnswer") || ""; } catch { return ""; }
  });

  // Forge hidden PDF unlock quiz state
  const [showForgeQuiz, setShowForgeQuiz] = useState(false);
  const [forgeAnswer, setForgeAnswer] = useState(() => {
    try { return localStorage.getItem("forgeHiddenAnswer") || ""; } catch { return ""; }
  });
  const [pendingForgeItem, setPendingForgeItem] = useState(null);

  // NEW: Forge inline "Hidden Alert -> God name" quiz state
  const [forgeGodAnswer, setForgeGodAnswer] = useState(() => {
    try { return localStorage.getItem("forgeGodAnswer") || ""; } catch { return ""; }
  });
  const [forgeGodSubmitted, setForgeGodSubmitted] = useState(false);
  const [vaultUnlocked, _setVaultUnlocked] = useState(() => {
    try { return localStorage.getItem("hr-vault-unlocked") === "1"; } catch { return false; }
  });
  const setVaultUnlocked = (v) => {
    _setVaultUnlocked(v);
    try { localStorage.setItem("hr-vault-unlocked", v ? "1" : "0"); } catch {}
  };

  // Athena riddle / lock state (for PO Photo 9)
  const [showAthenaQuiz, setShowAthenaQuiz] = useState(false);
  const [athenaAnswer, setAthenaAnswer] = useState("");
  const [pendingMedia, setPendingMedia] = useState(null); // which item is being unlocked

  // Read granted access map from localStorage once
  const grantedInitial = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("evidenceAccess") || "{}"); } catch { return {}; }
  }, []);
  const [granted, setGranted] = useState(grantedInitial);

  // Helper to persist granted map
  const persistGranted = (next) => {
    setGranted(next);
    try { localStorage.setItem("evidenceAccess", JSON.stringify(next)); } catch {}
  };

  // Track unlocked media
  const unlockedInitial = useMemo(() => {
    try {
      return {
        "po-9": localStorage.getItem("unlock-po-9") === "1",
        "if-hidden-alert": localStorage.getItem("unlock-if-hidden-alert") === "1",
      };
    } catch {
      return { "po-9": false, "if-hidden-alert": false };
    }
  }, []);
  const [unlocked, setUnlocked] = useState(unlockedInitial);

  const persistUnlock = (id) => {
    const next = { ...unlocked, [id]: true };
    setUnlocked(next);
    try { localStorage.setItem(`unlock-${id}`, "1"); } catch {}
  };

  // Opinion state (Parthenon Yes/No question)
  const [athenaOpinion, setAthenaOpinion] = useState(() => {
    try { return localStorage.getItem("athenaOpinion") || ""; } catch { return ""; }
  });

  const saveAthenaOpinion = (val) => {
    setAthenaOpinion(val);
    try { localStorage.setItem("athenaOpinion", val); } catch {}
  };

  // --- Readiness flags for the Parthenon opinion ---
  const [viewedAthenaVideo, setViewedAthenaVideo] = useState(() => {
    try { return localStorage.getItem("po-viewedAthenaVideo") === "1"; } catch { return false; }
  });
  const [openedCodex, setOpenedCodex] = useState(() => {
    try { return localStorage.getItem("po-openedCodex") === "1"; } catch { return false; }
  });
  const [viewedPhoto9, setViewedPhoto9] = useState(() => {
    try { return localStorage.getItem("po-viewedPhoto9") === "1"; } catch { return false; }
  });

  const markViewedAthenaVideo = () => {
    setViewedAthenaVideo(true);
    try { localStorage.setItem("po-viewedAthenaVideo", "1"); } catch {}
  };
  const markOpenedCodex = () => {
    setOpenedCodex(true);
    try { localStorage.setItem("po-openedCodex", "1"); } catch {}
  };
  const markViewedPhoto9 = () => {
    setViewedPhoto9(true);
    try { localStorage.setItem("po-viewedPhoto9", "1"); } catch {}
  };

  const parthenonReady = viewedAthenaVideo && openedCodex && viewedPhoto9;

  // If Photo 9 was previously unlocked but the viewed flag isn't set, sync it
  useEffect(() => {
    if (unlocked["po-9"] && !viewedPhoto9) {
      markViewedPhoto9();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked["po-9"]]);

  // Hecate Part I progress gate (for Part II)
  const [hecatePart1Seen85, setHecatePart1Seen85] = useState(() => {
    try { return localStorage.getItem("vc-hecate-1-85") === "1"; } catch { return false; }
  });
  const markHecate85 = () => {
    setHecatePart1Seen85(true);
    try { localStorage.setItem("vc-hecate-1-85", "1"); } catch {}
  };

  // pick up ?loc= from URL
  useEffect(() => {
    const loc = searchParams.get("loc");
    if (loc && LOCATIONS.some((l) => l.key === loc)) {
      if (granted[loc]) {
        setActiveKey(loc);
        setTimeout(() => contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
      } else {
        setPendingKey(loc);
        setShowGate(true);
      }
    }
  }, [searchParams, granted]);

  const selectLocation = (key) => {
    if (key === "all") {
      setActiveKey("all");
      setSearchParams({}, { replace: true });
      return;
    }
    if (granted[key]) {
      setActiveKey(key);
      setSearchParams({ loc: key }, { replace: true });
      setTimeout(() => contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
      return;
    }
    setPendingKey(key);
    setShowGate(true);
  };

  const handleGateSuccess = (key) => {
    const next = { ...granted, [key]: true };
    persistGranted(next);
    setActiveKey(key);
    setSearchParams({ loc: key }, { replace: true });
    setShowGate(false);
    setPendingKey(null);
    setTimeout(() => contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  // Called when the Zeus video reaches the end
  const handleZeusEnded = () => setShowZeusQuiz(true);

  // When a locked image is clicked (Photo 9)
  const handleLockedClick = (item) => {
    setPendingMedia(item);
    setShowAthenaQuiz(true);
  };

  // When the hidden PDF is clicked (to unlock)
  const handleForgeLockedClick = (item) => {
    setPendingForgeItem(item);
    setShowForgeQuiz(true);
  };

  const activeName =
    activeKey === "all"
      ? "All Locations"
      : LOCATIONS.find((l) => l.key === activeKey)?.name || "";

  // --- Hall of Records "Congrats" banner control ---
  const [showVaultCongrats, setShowVaultCongrats] = useState(false);
  useEffect(() => {
    if (activeKey === "hall-of-records" && vaultUnlocked) {
      const alreadyShown = sessionStorage.getItem("hr-vault-congrats-shown") === "1";
      if (!alreadyShown) {
        setShowVaultCongrats(true);
        try { sessionStorage.setItem("hr-vault-congrats-shown", "1"); } catch {}
      }
    } else {
      setShowVaultCongrats(false);
    }
  }, [activeKey, vaultUnlocked]);

  return (
    <main className="min-h-screen bg-black text-white">
      {/* Top strip */}
      <div className="w-full bg-blue-500 text-white text-center text-sm py-2">
        EVIDENCE ARCHIVE — Tap the map or choose a location.
      </div>

      <section className="max-w-6xl mx-auto px-6 py-8">
        {/* Header row */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Evidence</h1>
          <Link to="/" className="rounded-xl px-4 py-2 border border-white/20 hover:border-white/40 text-sm">
            ← Back to Home
          </Link>
        </div>

        {/* Interactive Map */}
        <div className="mt-6">
          <MapWithHotspots
            src={`${process.env.PUBLIC_URL}/locations-map.png`}
            locations={LOCATIONS}
            activeKey={activeKey}
            onSelect={selectLocation}
          />
        </div>

        {/* Location pills */}
        <div
          className="sticky top-12 z-30 bg-black/70 backdrop-blur mt-6 py-3 -mx-6 px-6 border-y border-white/10 overflow-x-auto"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <div className="flex gap-2" style={{ minWidth: "fit-content" }}>
            <button
              onClick={() => selectLocation("all")}
              className={`px-4 py-2 whitespace-nowrap rounded-full text-sm border transition ${
                activeKey === "all"
                  ? "bg-blue-500 text-white border-blue-500"
                  : "bg-white/5 text-white border-white/10 hover:border-white/30"
              }`}
            >
              All Locations
            </button>
            {LOCATIONS.map((l) => (
              <button
                key={l.key}
                onClick={() => selectLocation(l.key)}
                className={`px-4 py-2 whitespace-nowrap rounded-full text-sm border transition ${
                  activeKey === l.key
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white/5 text-white border-white/10 hover:border-white/30"
                }`}
              >
                {l.name}
                {!granted[l.key] && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                    Locked
                  </span>
                )}
              </button>
            ))}
          </div>
          <style>{`[style*="overflow-x:auto"]::-webkit-scrollbar { display: none; }`}</style>
        </div>

        {/* CONTENT */}
        <div ref={contentRef} className="mt-8">
          {/* Throne Room */}
          {activeKey === "throne-room" && THRONE_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{activeName}</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {THRONE_EVIDENCE.map((it) =>
                  it.type === "pdf" ? (
                    <PdfCard key={it.id} item={it} />
                  ) : it.type === "video" ? (
                    it.id === "tr-zeus-video" ? (
                      <VideoCard
                        key={it.id}
                        item={it}
                        onEnded={handleZeusEnded}
                        ref={zeusVideoRef}
                      />
                    ) : (
                      <VideoCard key={it.id} item={it} />
                    )
                  ) : (
                    <PhotoCard key={it.id} item={it} />
                  )
                )}
              </div>
            </>
          )}

          {/* Parthenon */}
          {activeKey === "parthenon" && PARTHENON_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{activeName}</h2>

              {/* Readiness gate */}
              <AthenaReadinessCard
                viewedAthenaVideo={viewedAthenaVideo}
                openedCodex={openedCodex}
                viewedPhoto9={viewedPhoto9}
                ready={parthenonReady}
                onAnswerNow={() => {
                  const el = document.getElementById("athena-opinion-anchor");
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                onHintScrollTo={(id) => {
                  const el = document.getElementById(id);
                  el?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              />

              {/* Opinion shows when ready */}
              {parthenonReady && (
                <div id="athena-opinion-anchor" className="mt-4">
                  <AthenaOpinionCard
                    value={athenaOpinion}
                    onChoose={(v) => { saveAthenaOpinion(v); }}
                    onGoOracle={() => selectLocation("oracles-chamber")}
                  />
                </div>
              )}

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-6">
                {PARTHENON_EVIDENCE.map((it) =>
                  it.type === "pdf" ? (
                    <PdfCard key={it.id} item={it} onOpen={() => it.id === "po-codex" && markOpenedCodex()} />
                  ) : it.type === "video" ? (
                    it.id === "po-athena-video" ? (
                      <div id="po-athena-video">
                        <VideoCard
                          key={it.id}
                          item={it}
                          onEnded={markViewedAthenaVideo}
                          onProgress85={markViewedAthenaVideo}
                        />
                      </div>
                    ) : (
                      <VideoCard key={it.id} item={it} />
                    )
                  ) : it.id === "po-9" ? (
                    <div id="po-9">
                      <LockedPhotoCard
                        key={it.id}
                        item={it}
                        unlocked={!!unlocked["po-9"]}
                        onClickLocked={() => handleLockedClick(it)}
                      />
                    </div>
                  ) : (
                    <PhotoCard key={it.id} item={it} />
                  )
                )}
              </div>
            </>
          )}

          {/* Oracle's Chamber */}
          {activeKey === "oracles-chamber" && ORACLE_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{activeName}</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {ORACLE_EVIDENCE.map((it) =>
                  it.type === "pdf" ? (
                    <PdfCard key={it.id} item={it} />
                  ) : it.type === "video" ? (
                    <VideoCard key={it.id} item={it} />
                  ) : (
                    <PhotoCard key={it.id} item={it} />
                  )
                )}
              </div>
            </>
          )}

          {/* Hall of Records */}
          {activeKey === "hall-of-records" && HALL_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-2">{activeName}</h2>

              {/* Congrats banner when vault is unlocked */}
              {showVaultCongrats && (
                <div className="mb-4 rounded-xl border border-green-600/40 bg-green-600/10 px-4 py-3">
                  <p className="text-sm">
                    <span className="font-semibold">Congratulations!</span> You have managed to uncover the actual timestamps of Hermes.{" "}
                    Open the document to review them.
                  </p>
                </div>
              )}

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {/* Conditionally show the new VAULT PDF first when unlocked */}
                {vaultUnlocked && (
                  <PdfCard
                    item={{
                      id: "hr-vault-pdf",
                      type: "pdf",
                      title: "Vault Records",
                      src: `${process.env.PUBLIC_URL}/evidences/vault.pdf`,
                    }}
                  />
                )}

                {HALL_EVIDENCE.map((it) => {
                  if (it.id === "hr-hermes-video") {
                    // Inline question card stacked above the Hermes video
                    return (
                      <div id="hr-hermes-video" key={`${it.id}-wrap`} className="space-y-3">
                        <HermesInlineQuizCard
                          value={hermesAnswer}
                          onChange={setHermesAnswer}
                          onGotoForge={() => selectLocation("ironheart-forge")}
                        />
                        <VideoCard item={it} ref={hermesVideoRef} />
                      </div>
                    );
                  }
                  if (it.type === "video") return <VideoCard key={it.id} item={it} />;
                  if (it.type === "pdf") return <PdfCard key={it.id} item={it} />;
                  return <PhotoCard key={it.id} item={it} />;
                })}
              </div>
            </>
          )}

          {/* Ironheart Forge */}
          {activeKey === "ironheart-forge" && FORGE_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{activeName}</h2>

              {/* NEW: Inline Forge God Name Quiz (above videos) */}
              <ForgeGodQuizCard
                value={forgeGodAnswer}
                onChange={setForgeGodAnswer}
                onSuccess={() => {
                  if (!vaultUnlocked) {
                    setVaultUnlocked(true);
                  }
                }}
                onGoHall={() => selectLocation("hall-of-records")}
              />

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-4">
                {FORGE_EVIDENCE.map((it) => {
                  if (it.id === "if-hidden-alert") {
                    return (
                      <LockedPdfCard
                        key={it.id}
                        item={it}
                        unlocked={!!unlocked["if-hidden-alert"]}
                        onClickLocked={() => handleForgeLockedClick(it)}
                      />
                    );
                  }
                  if (it.type === "video") return <VideoCard key={it.id} item={it} />;
                  if (it.type === "pdf") return <PdfCard key={it.id} item={it} />;
                  return <PhotoCard key={it.id} item={it} />;
                })}
              </div>
            </>
          )}

          {/* Sacred Glade */}
          {activeKey === "sacred-glade" && GLADE_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{activeName}</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {GLADE_EVIDENCE.map((it) =>
                  it.type === "pdf" ? (
                    <PdfCard key={it.id} item={it} />
                  ) : it.type === "video" ? (
                    <VideoCard key={it.id} item={it} />
                  ) : (
                    <PhotoCard key={it.id} item={it} />
                  )
                )}
              </div>
            </>
          )}

          {/* Veiled Crossroad */}
          {activeKey === "veiled-crossroad" && VEILED_EVIDENCE.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{activeName}</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {VEILED_EVIDENCE.map((it) => {
                  if (it.id === "vc-burnt-scroll") {
                    // Password-gated item
                    return (
                      <PasswordProtectedVideoCard
                        key={it.id}
                        item={it}
                        expectedPassword="Ancientpact05"
                        promptText="Please enter the password before you access the evidence:"
                        storageKey="unlock-vc-burnt-scroll"
                      />
                    );
                  }
                  if (it.id === "vc-hecate-1") {
                    // Track 85% progress to unlock Part II
                    return (
                      <div id="vc-hecate-1" key={`${it.id}-wrap`}>
                        <VideoCard item={it} onProgress85={markHecate85} />
                      </div>
                    );
                  }
                  if (it.id === "vc-hecate-2") {
                    // Lock until Part I has been watched to 85%
                    return hecatePart1Seen85 ? (
                      <VideoCard key={it.id} item={it} />
                    ) : (
                      <LockedUntilCard
                        key={it.id}
                        title={it.title}
                        message="Watch Hecate’s Testimony — Part I (at least 85%) to unlock Part II."
                        scrollToId="vc-hecate-1"
                      />
                    );
                  }

                  // defaults
                  if (it.type === "pdf") return <PdfCard key={it.id} item={it} />;
                  if (it.type === "video") return <VideoCard key={it.id} item={it} />;
                  return <PhotoCard key={it.id} item={it} />;
                })}
              </div>
            </>
          )}

          {/* Other locations: render nothing until you add items */}
        </div>
      </section>

      {/* Password Gate Modal */}
      <PasswordGateModal
        open={showGate}
        onClose={() => { setShowGate(false); setPendingKey(null); }}
        locationKey={pendingKey}
        locationName={LOCATIONS.find((l) => l.key === pendingKey)?.name}
        expectedPassword={pendingKey ? PASSWORDS[pendingKey] : ""}
        onSuccess={() => pendingKey && handleGateSuccess(pendingKey)}
      />

      {/* Zeus Quiz Modal */}
      <ZeusQuizModal
        open={showZeusQuiz}
        value={zeusAnswer}
        onChange={setZeusAnswer}
        onClose={() => setShowZeusQuiz(false)}
        onRewatch={() => {
          setShowZeusQuiz(false);
          setTimeout(() => {
            zeusVideoRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => {
              try { zeusVideoRef.current?.focus?.(); zeusVideoRef.current?.play?.(); } catch {}
            }, 400);
          }, 50);
        }}
        onGotoParthenon={() => { setShowZeusQuiz(false); selectLocation("parthenon"); }}
      />

      {/* Forge Hidden PDF — Time Quiz Modal */}
      <ForgeTimeQuizModal
        open={showForgeQuiz}
        value={forgeAnswer}
        onChange={setForgeAnswer}
        onClose={() => { setShowForgeQuiz(false); setForgeAnswer(""); setPendingForgeItem(null); }}
        onSuccess={() => {
          if (pendingForgeItem?.id) {
            persistUnlock(pendingForgeItem.id);
            try { window.open(pendingForgeItem.src, "_blank", "noopener,noreferrer"); } catch {}
          }
          setShowForgeQuiz(false);
          setForgeAnswer("");
          setPendingForgeItem(null);
        }}
      />

      {/* Athena Quiz Modal (locks Parthenon Photo 9) */}
      <AthenaQuizModal
        open={showAthenaQuiz}
        value={athenaAnswer}
        onChange={setAthenaAnswer}
        onClose={() => {
          setShowAthenaQuiz(false);
          setAthenaAnswer("");
          setPendingMedia(null);
        }}
        onSuccess={() => {
          if (pendingMedia?.id) {
            persistUnlock(pendingMedia.id);
            if (pendingMedia.id === "po-9") {
              markViewedPhoto9();
            }
            try { window.open(pendingMedia.src, "_blank", "noopener,noreferrer"); } catch {}
          }
          setShowAthenaQuiz(false);
          setAthenaAnswer("");
          setPendingMedia(null);
        }}
      />
    </main>
  );
}

function MapWithHotspots({ src, locations, activeKey, onSelect }) {
  return (
    <div className="relative w-full max-w-4xl mx-auto">
      <img
        src={src}
        alt="Olympus map with key locations"
        className="w-full h-auto rounded-2xl border border-white/10"
        onError={(e) => {
          e.currentTarget.alt = "Map failed to load — check /public/locations-map.png";
          e.currentTarget.style.opacity = 0.5;
        }}
      />
      {locations.map((l) => (
        <button
          key={l.key}
          onClick={() => onSelect(l.key)}
          aria-label={l.name}
          title={l.name}
          style={{ left: l.x, top: l.y }}
          className={`absolute -translate-x-1/2 -translate-y-1/2
                      h-5 w-5 rounded-full ring-2 ring-black/50 shadow
                      ${activeKey === l.key ? "bg-red-500" : "bg-white/80 hover:bg-red-300"}
                      focus:outline-none focus:ring-4 focus:ring-red-400/50`}
        />
      ))}
    </div>
  );
}


function PhotoCard({ item }) {
  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-white/30 transition">
      <a href={item.src} target="_blank" rel="noreferrer" className="block">
        <div className="aspect-video bg-black/40 overflow-hidden">
          <img src={item.src} alt={item.title} className="w-full h-full object-cover" />
        </div>
      </a>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{item.title}</h3>
        <a
          href={item.src}
          target="_blank"
          rel="noreferrer"
          className="text-xs underline underline-offset-4 hover:no-underline text-white/80"
        >
          View full size
        </a>
      </div>
    </article>
  );
}

function PdfCard({ item, onOpen }) {
  const cardRef = useRef(null);

  // Count Codex as "opened" when it comes into view (plus still on click)
  useEffect(() => {
    if (!cardRef.current || item.id !== "po-codex") return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        try { onOpen?.(); } catch {}
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, [item.id, onOpen]);

  return (
    <article ref={cardRef} className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-white/30 transition">
      <a
        href={item.src}
        target="_blank"
        rel="noreferrer"
        className="block"
        onClick={() => onOpen?.()}
        id={item.id === "po-codex" ? "po-codex" : undefined}
      >
        <div className="aspect-video bg-black/40 flex items-center justify-center">
          {/* Simple inline PDF icon */}
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" className="text-white/60" strokeWidth="1.5"/>
            <path d="M14 2v6h6" stroke="currentColor" className="text-white/60" strokeWidth="1.5"/>
            <text x="7" y="16" fontSize="6" fill="currentColor" className="text-white/80">PDF</text>
          </svg>
        </div>
      </a>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{item.title}</h3>
        <a
          href={item.src}
          target="_blank"
          rel="noreferrer"
          className="text-xs underline underline-offset-4 hover:no-underline text-white/80"
          onClick={() => onOpen?.()}
        >
          Open PDF
        </a>
      </div>
    </article>
  );
}

// Forward-ref so we can control video (scroll/play) from parent
const VideoCard = forwardRef(function VideoCard({ item, onEnded, onProgress85 }, ref) {
  const seen85Ref = useRef(false);

  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-white/30 transition">
      <div className="aspect-video bg-black/40 overflow-hidden">
        <video
          controls
          preload="metadata"
          className="w-full h-full object-cover"
          onEnded={onEnded}
          onTimeUpdate={(e) => {
            if (!onProgress85 || seen85Ref.current) return;
            const v = e.currentTarget;
            if (v.duration && v.currentTime / v.duration >= 0.85) {
              seen85Ref.current = true;
              try { onProgress85(); } catch {}
            }
          }}
          ref={ref}
        >
          <source src={item.src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{item.title}</h3>
        <a
          href={item.src}
          download
          className="text-xs underline underline-offset-4 hover:no-underline text-white/80"
        >
          Download video
        </a>
      </div>
    </article>
  );
});

/** Locked Photo Card (blur + lock overlay for Parthenon Photo 9) */
function LockedPhotoCard({ item, unlocked, onClickLocked }) {
  const onClick = (e) => {
    if (!unlocked) {
      e.preventDefault();
      onClickLocked?.();
    }
  };

  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-white/30 transition relative">
      <a
        href={item.src}
        target={unlocked ? "_blank" : undefined}
        rel="noreferrer"
        className="block"
        onClick={onClick}
      >
        <div className="aspect-video bg-black/40 overflow-hidden relative">
          <img
            src={item.src}
            alt={item.title}
            className={`w-full h-full object-cover transition ${unlocked ? "" : "blur-lg scale-[1.02] opacity-80"}`}
          />
        </div>
      </a>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{item.title}</h3>
        {unlocked ? (
          <a
            href={item.src}
            target="_blank"
            rel="noreferrer"
            className="text-xs underline underline-offset-4 hover:no-underline text-white/80"
          >
            View full size
          </a>
        ) : (
          <button
            onClick={onClickLocked}
            className="text-xs underline underline-offset-4 hover:no-underline text-white/80"
          >
            Unlock
          </button>
        )}
      </div>
    </article>
  );
}

/** Locked PDF Card (blur-style placeholder with PDF icon) */
function LockedPdfCard({ item, unlocked, onClickLocked }) {
  const handleClick = (e) => {
    if (!unlocked) {
      e.preventDefault();
      onClickLocked?.();
    }
  };

  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 transition relative">
      <a
        href={item.src}
        target={unlocked ? "_blank" : undefined}
        rel="noreferrer"
        className="block"
        onClick={handleClick}
      >
        <div className="aspect-video bg-black/40 flex items-center justify-center">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" className={`${unlocked ? "opacity-100" : "opacity-60"}`} aria-hidden>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5"/>
            <text x="7" y="16" fontSize="6" fill="currentColor">PDF</text>
          </svg>
        </div>
      </a>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{item.title}</h3>
        {unlocked ? (
          <a href={item.src} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4 hover:no-underline text-white/80">
            Open PDF
          </a>
        ) : (
          <button onClick={onClickLocked} className="text-xs underline underline-offset-4 hover:no-underline text-white/80">
            Unlock
          </button>
        )}
      </div>
    </article>
  );
}

/** Small helper card to show a locked item until a condition is met */
function LockedUntilCard({ title, message, scrollToId }) {
  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 transition relative">
      <div className="aspect-video bg-black/40 flex items-center justify-center">
        <span className="text-sm text-white/80 px-4 text-center">{message}</span>
      </div>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        {scrollToId && (
          <button
            className="text-xs underline underline-offset-4 hover:no-underline text-white/80"
            onClick={() => {
              const el = document.getElementById(scrollToId);
              el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
          >
            Go to Part I
          </button>
        )}
      </div>
    </article>
  );
}

/** Per-item Password-protected video card (used for Burnt Scroll) */
function PasswordProtectedVideoCard({ item, expectedPassword, promptText, storageKey = `unlock-${item.id}` }) {
  const [open, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });

  const handleSuccess = () => {
    setUnlocked(true);
    try { localStorage.setItem(storageKey, "1"); } catch {}
    setOpen(false);
  };

  if (unlocked) return <VideoCard item={item} />;

  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 transition relative">
      <div className="aspect-video bg-black/40 flex items-center justify-center">
        <button className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm" onClick={() => setOpen(true)}>
          Enter password to view
        </button>
      </div>
      <div className="p-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{item.title}</h3>
        <span className="text-xs text-white/60">Locked</span>
      </div>

      <ItemPasswordModal
        open={open}
        onClose={() => setOpen(false)}
        promptText={promptText}
        expectedPassword={expectedPassword}
        onSuccess={handleSuccess}
      />
    </article>
  );
}

function ItemPasswordModal({ open, onClose, promptText, expectedPassword, onSuccess }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setValue(""); setError(""); } }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e?.preventDefault();
    if ((value || "") === expectedPassword) {
      setError("");
      onSuccess?.();
    } else {
      setError("Incorrect password. Try again.");
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" role="dialog" aria-modal="true" onClick={onBackdrop}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-900 shadow-xl">
        <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Password Required</h3>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-blue-400/50" aria-label="Close" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" /></svg>
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <p className="text-sm text-white/80">{promptText}</p>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-white/60">Password</span>
            <input
              type="password"
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
              placeholder="Enter password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              aria-label="Password"
            />
          </label>

          {error && <div className="text-sm text-red-400">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm">Unlock</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Password Gate Modal (location access) */
function PasswordGateModal({ open, onClose, locationKey, locationName, expectedPassword, onSuccess }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setValue(""); setError(""); }
  }, [open, locationKey]);

  if (!open) return null;

  const submit = (e) => {
    e?.preventDefault();
    const ok = value.trim().toLowerCase() === (expectedPassword || "").toLowerCase();
    if (ok) {
      setError("");
      onSuccess?.();
    } else {
      setError("Incorrect code. Try again.");
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      role="dialog"
      aria-modal="true"
      onClick={onBackdrop}
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-900 shadow-xl">
        <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Enter Access Code</h3>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-blue-400/50"
            aria-label="Close"
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <p className="text-sm text-white/80">
            Location: <span className="font-medium">{locationName || locationKey}</span>
          </p>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-white/60">Code</span>
            <input
              type="text"
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
              placeholder="Enter code (e.g., t_room)"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              aria-label="Access code"
            />
          </label>

          {error && <div className="text-sm text-red-400">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm"
            >
              Unlock
            </button>
          </div>
        </form>

        <div className="px-5 pb-5">
          <p className="text-[11px] text-white/40">
            Hint: codes use the pattern <code className="text-white/60">{"<first-letter>_room"}</code>. Example: Throne Room → <code className="text-white/60">t_room</code>.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Zeus Quiz Modal */
function ZeusQuizModal({ open, value, onChange, onClose, onRewatch, onGotoParthenon }) {
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => { if (open) { setSubmitted(false); setIsCorrect(false); } }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e?.preventDefault();
    const normalized = (value || "").trim().toLowerCase();
    const correct = normalized === "athena";
    setSubmitted(true);
    setIsCorrect(correct);
    try { localStorage.setItem("zeusTestimonyAnswer", value || ""); } catch {}
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  if (submitted && isCorrect) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" role="dialog" aria-modal="true" onClick={onBackdrop}>
        <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-900 shadow-xl">
          <div className="border-b border-white/10 px-5 py-4"><h3 className="text-lg font-semibold">Correct!</h3></div>
          <div className="px-5 py-5 space-y-4">
            <p className="text-white/80">Now you can go and visit the <span className="font-semibold">Parthenon of Olympus</span> location and the code to access that is <code className="text-white">p_room</code>.</p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm">Close</button>
              <button type="button" onClick={onGotoParthenon} className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm">Go to Parthenon</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" role="dialog" aria-modal="true" onClick={onBackdrop}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-900 shadow-xl">
        <div className="border-b border-white/10 px-5 py-4">
          <h3 className="text-lg font-semibold">Quick Check</h3>
          <p className="text-sm text-white/70 mt-1">Which god did Zeus refer to in their testimony?</p>
          <p className="text-[11px] text-white/40 mt-1">Tip: one word, lowercase (e.g., <code>hera</code>)</p>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-white/60">Your answer</span>
            <input
              type="text"
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
              placeholder="athena"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              autoFocus
            />
          </label>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onRewatch} className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm">Rewatch video</button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm">Submit answer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Hermes Inline Quiz Card (shows above the video) */
function HermesInlineQuizCard({ value, onChange, onGotoForge }) {
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const submit = (e) => {
    e?.preventDefault();
    const normalized = (value || "").trim().toLowerCase();
    const correct = normalized === "forge"; // correct answer not shown to player
    setSubmitted(true);
    setIsCorrect(correct);
    try { localStorage.setItem("hermesTestimonyAnswer", value || ""); } catch {}
  };

  return (
    <article className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
      <h3 className="text-base font-semibold">Hermes Records Check</h3>
      <p className="text-sm text-white/80 mt-1">
        Based on the Hermes records, which location did Hermes visit during the time of murder?
      </p>
      <p className="text-[11px] text-white/45">
        Please enter only the <em>name of the location</em> exactly as shown on the records.
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
          placeholder="type the location name"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <button
          type="submit"
          className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm"
        >
          Submit
        </button>
      </form>

      {submitted && !isCorrect && (
        <p className="mt-2 text-sm text-red-400">
          Please read the Hermes records carefully.
        </p>
      )}

      {submitted && isCorrect && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-white/85">
            Correct. You can proceed to the final location, <span className="font-semibold">Ironheart Forge</span>.
            Use the access code <code className="text-white">i_room</code>.
          </p>
          <button
            className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm"
            onClick={onGotoForge}
          >
            Go to Ironheart Forge
          </button>
        </div>
      )}
    </article>
  );
}

/** NEW: Inline quiz at Ironheart Forge for the Hidden Alert revealed name */
function ForgeGodQuizCard({ value, onChange, onSuccess, onGoHall }) {
  const [submitted, setSubmitted] = useState(false);
  const normalizedCorrect = (FORGE_ALERT_GOD || "").trim().toLowerCase();

  const submit = (e) => {
    e?.preventDefault();
    const ans = (value || "").trim().toLowerCase();
    const correct = ans && ans === normalizedCorrect;
    setSubmitted(true);
    try { localStorage.setItem("forgeGodAnswer", value || ""); } catch {}
    if (correct) onSuccess?.();
  };

  const answeredCorrectly = submitted && (value || "").trim().toLowerCase() === normalizedCorrect;

  return (
    <article className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
      <h3 className="text-base font-semibold">Hidden Alert — Final Check</h3>
      <p className="text-sm text-white/80 mt-1">
        Answer only if you have solved the hidden alert challenge.
      </p>
      <p className="text-sm text-white/80 mt-1">
        <span className="font-semibold">Question:</span> Which God&apos;s name is revealed at the hidden alert message?
      </p>

      <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
          placeholder="type the god's name"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm">
          Submit
        </button>
      </form>

      {submitted && !answeredCorrectly && (
        <p className="mt-2 text-sm text-red-400">That doesn’t match the Hidden Alert. Check it again.</p>
      )}

      {answeredCorrectly && (
        <div className="mt-3 rounded-xl border border-blue-400/40 bg-blue-400/10 px-4 py-3">
          <p className="text-sm">
            <span className="font-semibold">Go visit the Hall of Records</span> — a new item has been unlocked.
          </p>
          <button
            className="mt-2 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-sm"
            onClick={onGoHall}
          >
            Go to Hall of Records
          </button>
        </div>
      )}
    </article>
  );
}

/** NEW: Forge Hidden PDF Time Quiz Modal */
function ForgeTimeQuizModal({ open, value, onChange, onClose, onSuccess }) {
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => { if (open) { setSubmitted(false); setIsCorrect(false); } }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e?.preventDefault();
    const normalized = (value || "").trim();
    // Expect exact HH:MM format — correct time is not displayed here
    const correct = normalized === "11:15";
    setSubmitted(true);
    setIsCorrect(correct);
    try { localStorage.setItem("forgeHiddenAnswer", value || ""); } catch {}
    if (correct) onSuccess?.();
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" role="dialog" aria-modal="true" onClick={onBackdrop}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-neutral-900 shadow-xl">
        <div className="border-b border-white/10 px-5 py-4">
          <h3 className="text-lg font-semibold">Unlock Hidden Alert</h3>
          <p className="text-sm text-white/80 mt-1">
            During Hephaestus’ testimony, he mentioned that the time had been tampered and moved one hour backwards.
            Based on this statement and his Forgemaster&rsquo;s Diagnostic Report, what was the <strong>actual time</strong> that Hermes visited him at his forge?
          </p>
          <p className="text:[11px] text-white/40 mt-1">Provide the time as HH:MM, e.g., <code>07:00</code>, <code>08:15</code>.</p>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-white/60">Your answer (HH:MM)</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="^[0-2]\d:\d{2}$"
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
              placeholder="HH:MM"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              autoFocus
            />
          </label>

          {submitted && !isCorrect && (
            <p className="text-sm text-red-400">
              Not quite. Recheck Hephaestus’ statement about moving the clock one hour back and the diagnostic report.
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm">Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Athena Opinion card */
function AthenaOpinionCard({ value, onChoose, onGoOracle }) {
  const chosen = value === "yes" || value === "no";

  return (
    <article className="mt-2 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.03] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Your Call on Athena</h3>
          <p className="text-sm text-white/80 mt-1">
            Do you believe Athena&rsquo;s testimony based on the evidence provided?
          </p>
        </div>
        {chosen && (
          <button
            className="text-xs px-3 py-1 rounded-full border border-white/10 hover:border-white/30"
            onClick={() => onChoose("")}
            title="Change your answer"
          >
            Change
          </button>
        )}
      </div>

      {!chosen ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm" onClick={() => onChoose("yes")}>
            Yes
          </button>
          <button className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm" onClick={() => onChoose("no")}>
            No
          </button>
        </div>
      ) : value === "yes" ? (
        <div className="mt-4 space-y-3">
          <p className="text-white/90">
            <span className="font-medium">This looks like a right choice...</span>{" "}
            <span className="text-white/70">or maybe not?</span>
          </p>
          <p className="text-white/80">
            Go to the next location, <span className="font-semibold">Oracle&rsquo;s Chamber</span> and add the password{" "}
            <code className="text-white">o_room</code> to enter the location. Perhaps you might collect more evidence there to continue with your investigation.
          </p>
          <div>
            <button className="mt-2 px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm" onClick={onGoOracle}>
              Go to Oracle&rsquo;s Chamber
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-white/90">
            <span className="font-medium">Has Athena outplayed you, or have you outplayed her??</span>
          </p>
          <p className="text-white/80">
            Go to the next location, <span className="font-semibold">Oracle&rsquo;s Chamber</span> and add the password{" "}
            <code className="text-white">o_room</code> to enter the location. Perhaps you might collect more evidence there to continue with your investigation.
          </p>
          <div>
            <button className="mt-2 px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm" onClick={onGoOracle}>
              Go to Oracle&rsquo;s Chamber
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

/** Readiness card — disables "Answer now" until checks pass */
function AthenaReadinessCard({ viewedAthenaVideo, openedCodex, viewedPhoto9, ready, onAnswerNow, onHintScrollTo }) {
  const [showWhy, setShowWhy] = useState(false);

  const missing = [
    !viewedAthenaVideo && { id: "po-athena-video", label: "Watch Athena’s Testimony (video)" },
    !openedCodex && { id: "po-codex", label: "Open the Athenian Codex (PDF)" },
    !viewedPhoto9 && { id: "po-9", label: "Unlock and view Photo 9" },
  ].filter(Boolean);

  return (
    <article className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
      <h3 className="text-base font-semibold">Before you answer…</h3>
      <p className="text-sm text-white/80 mt-1">
        Click only if you&rsquo;ve studied the evidence and you&rsquo;re sure about Athena&rsquo;s alibi.
      </p>

      <div className="mt-3 flex items-center gap-3">
        <button
          className={`px-4 py-2 rounded-xl text-sm ${ready ? "bg-blue-500 hover:bg-blue-600" : "bg-white/10 text-white/60 cursor-not-allowed"}`}
          disabled={!ready}
          onClick={onAnswerNow}
        >
          Answer now
        </button>

        <button
          className="text-xs underline underline-offset-4 text-white/70 hover:text-white"
          onClick={() => setShowWhy((s) => !s)}
        >
          {showWhy ? "Hide" : "Why can’t I answer yet?"}
        </button>
      </div>

      {showWhy && !ready && (
        <ul className="mt-3 text-sm text-white/80 list-disc pl-5 space-y-1">
          {missing.map((m) => (
            <li key={m.id}>
              <button
                className="underline underline-offset-2 hover:no-underline"
                onClick={() => onHintScrollTo?.(m.id)}
                title="Jump to item"
              >
                {m.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showWhy && ready && (
        <p className="mt-3 text-sm text-white/80">You&rsquo;re all set—go ahead and answer.</p>
      )}
    </article>
  );
}

/** Athena Quiz Modal (for Parthenon Photo 9) — SINGLE definition */
function AthenaQuizModal({ open, value, onChange, onClose, onSuccess }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setSubmitted(false); setError(""); } }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e?.preventDefault();
    const normalized = (value || "").trim();
    const isNum = /^\d+$/.test(normalized);
    const correct = isNum && parseInt(normalized, 10) === 11;

    setSubmitted(true);
    if (correct) {
      setError("");
      onSuccess?.();
    } else {
      setError("study again the evidence together with the greek numeric values");
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      role="dialog"
      aria-modal="true"
      onClick={onBackdrop}
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-900 shadow-xl">
        <div className="border-b border-white/10 px-5 py-4">
          <h3 className="text-lg font-semibold">Answer to Unlock</h3>
          <p className="text-sm text-white/80 mt-1">
            <span className="font-medium">If A = 1 and I = 9</span> what is <span className="font-medium">IB</span>?
          </p>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-white/60">Your answer (number)</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-blue-400"
              placeholder="11"
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              autoFocus
            />
          </label>

          {submitted && error && (
            <p className="text-sm text-red-400">{error}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-white/10 hover:border-white/30 text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm">Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
}
